'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Phone, Mail, Coffee, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { sendContactEmail } from '@/app/actions/Email/actions';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formFeedback, setFormFeedback] = useState({
    success: true,
    message: "Thanks for reaching out! We'll get back to you shortly.",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email using server action
      const result = await sendContactEmail(formData);

      setFormFeedback(result);
      setIsSubmitted(true);

      if (result.success) {
        // Reset form if successful
        setFormData({ name: '', email: '', subject: '', message: '' });

        // Reset success message after 5 seconds
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        // If error, allow user to try again
        setTimeout(() => setIsSubmitted(false), 5000);
      }
    } catch (error) {
      console.error(error);
      setFormFeedback({
        success: false,
        message: 'An unexpected error occurred. Please try again later.',
      });
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <section className="bg-primary/10 py-12">
        <div className="container mx-auto px-4">
          <Link href="/">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Got a question, a brilliant idea, or just want to say hi? We'd love to hear from you!
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Column - Contact Info */}
            <div className="space-y-8">
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Badge variant="outline" className="px-3 py-1 text-sm font-medium bg-primary/5">
                    Before You Reach Out
                  </Badge>
                </h2>
                <p className="text-gray-700">
                  If your question is about hosting, we've got you covered! Head over to our{' '}
                  <Link href="/hosting" className="text-primary font-medium hover:underline">
                    Hosting Page
                  </Link>{' '}
                  first – chances are, the answer's already waiting for you there (and we promise
                  it's worth a click).
                </p>
                <p className="text-gray-700">
                  But if you still need us (or just really enjoy human interaction), here's how to
                  get in touch:
                </p>

                <h2 className="text-2xl font-bold mb-4 mt-8 flex items-center gap-2">
                  Get In Touch
                </h2>
              </div>

              <Card className="border-0 shadow-md overflow-hidden bg-gradient-to-br from-primary/5 to-primary/10">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Email</h3>
                        <a href="mailto:hello@kawkb.com" className="text-primary hover:underline">
                          hello@kawkb.com
                        </a>
                        <p className="text-sm text-gray-500 mt-1">We reply within 24 hours</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Phone</h3>
                        <a href="tel:+201021514433" className="text-primary hover:underline">
                          +201021514433
                        </a>
                        <p className="text-sm text-gray-500 mt-1">Available 9am-6pm EET</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <AlertCircle className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Got an Urgent Issue?</h3>
                        <p className="text-gray-700">
                          No problem, we've got your back! Just call us, and we'll fix it quicker
                          than you can find your keys.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="relative p-6 bg-primary/5 rounded-lg mt-8 border border-primary/10">
                <div className="flex items-center gap-2 mb-2">
                  <Coffee className="h-5 w-5 text-amber-600" />
                  <h3 className="font-semibold text-lg">Fun Fact:</h3>
                </div>
                <p className="text-gray-700 italic">
                  Did you know? 87% of our support team's coffee consumption directly correlates
                  with answering your awesome messages. Keep us caffeinated – reach out today!
                </p>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Drop Us a Message</h2>
              <p className="text-gray-700 mb-6">
                We're like that friend who actually replies. Fill out the form below, and we'll get
                back to you faster than your morning coffee kicks in.
              </p>

              {isSubmitted ? (
                <div
                  className={`${formFeedback.success ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'} border rounded-lg p-6 text-center`}
                >
                  <h3
                    className={`text-xl font-semibold ${formFeedback.success ? 'text-green-700' : 'text-red-700'} mb-2`}
                  >
                    {formFeedback.success ? 'Message Sent!' : 'Oops!'}
                  </h3>
                  <p className={formFeedback.success ? 'text-green-600' : 'text-red-600'}>
                    {formFeedback.message}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Subject
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us what's on your mind..."
                      rows={6}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full py-5" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
